import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, CaretRight, Target } from "@phosphor-icons/react";

const LEVEL_STYLE = {
  company: "bg-adb-navy text-white border-adb-navy",
  department: "bg-emerald-100 text-emerald-800 border-emerald-300",
  team: "bg-blue-100 text-blue-800 border-blue-300",
  individual: "bg-zinc-100 text-zinc-800 border-zinc-300",
};

function currentQuarter() {
  const d = new Date();
  return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
}

const QUARTERS = (() => {
  const now = new Date();
  const arr = [];
  for (let i = 2; i >= -2; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
    arr.push(`${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`);
  }
  return Array.from(new Set(arr));
})();

export default function OKR() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  const isHR = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);

  const [quarter, setQuarter] = useState(currentQuarter());
  const [tree, setTree] = useState({ quarter, roots: [] });
  const [flat, setFlat] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableQuarters, setAvailableQuarters] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", level: "individual", quarter,
    parent_objective_id: "", owner_employee_id: "", department: "",
    key_results: [{ label: "", metric: "", target: 100, current: 0, unit: "%" }],
  });

  // On first load, discover quarters that have objectives and jump to the newest one if current is empty.
  useEffect(() => {
    api.get("/okr/objectives", { params }).then(({ data }) => {
      const qs = Array.from(new Set(data.map((o) => o.quarter))).sort();
      setAvailableQuarters(qs);
      if (qs.length && !qs.includes(quarter)) setQuarter(qs[qs.length - 1]);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [tenantId]);

  const load = () => {
    api.get("/okr/tree", { params: { ...params, quarter } }).then(({ data }) => setTree(data)).catch(() => {});
    api.get("/okr/objectives", { params: { ...params, quarter } }).then(({ data }) => setFlat(data)).catch(() => {});
    if (isHR) api.get("/employees", { params }).then(({ data }) => setEmployees(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [quarter, tenantId]);

  const save = async () => {
    try {
      const payload = { ...form, quarter };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      payload.key_results = form.key_results.filter((k) => k.label).map((k) => ({ ...k, target: Number(k.target || 0), current: Number(k.current || 0) }));
      if (!payload.parent_objective_id) delete payload.parent_objective_id;
      if (!payload.owner_employee_id) delete payload.owner_employee_id;
      await api.post("/okr/objectives", payload);
      toast.success("Objective created");
      setOpen(false);
      setForm({ title: "", description: "", level: "individual", quarter, parent_objective_id: "", owner_employee_id: "", department: "", key_results: [{ label: "", metric: "", target: 100, current: 0, unit: "%" }] });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const overallProgress = useMemo(() => {
    if (!flat.length) return 0;
    return Math.round(flat.reduce((a, o) => a + (o.progress_percent || 0), 0) / flat.length);
  }, [flat]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Strategy · OKRs</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Objectives & Key Results</h1>
          <p className="text-sm text-zinc-500 mt-2">Cascade goals from company → department → team → individual. Each objective tracks measurable KRs.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Quarter</Label>
            <Select value={quarter} onValueChange={setQuarter}>
              <SelectTrigger className="rounded-sm w-[130px]" data-testid="okr-quarter-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from(new Set([...QUARTERS, ...availableQuarters])).sort().map((q) => <SelectItem key={q} value={q}>{q}{availableQuarters.includes(q) && " ●"}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isHR && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="okr-add-btn"><Plus size={16} className="mr-1.5" /> New objective</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">Create objective</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="okr-title-input" /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Level</Label>
                    <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="okr-level-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{["company", "department", "team", "individual"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Cascade from</Label>
                    <Select value={form.parent_objective_id} onValueChange={(v) => setForm({ ...form, parent_objective_id: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="okr-parent-select"><SelectValue placeholder="(top-level)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">(top-level)</SelectItem>
                        {flat.filter((f) => f.level !== form.level).map((f) => <SelectItem key={f.id} value={f.id}>{f.level}: {f.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.level !== "company" && (
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Owner</Label>
                      <Select value={form.owner_employee_id} onValueChange={(v) => setForm({ ...form, owner_employee_id: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="okr-owner-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Key Results</Label>
                      <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => setForm({ ...form, key_results: [...form.key_results, { label: "", metric: "", target: 100, current: 0, unit: "%" }] })}><Plus size={12} className="mr-1" /> KR</Button>
                    </div>
                    <div className="space-y-2 max-h-[35vh] overflow-y-auto">
                      {form.key_results.map((k, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end border border-zinc-200 rounded-sm p-2">
                          <div className="col-span-4"><Label className="text-[10px]">Label</Label><Input value={k.label} onChange={(e) => setForm({ ...form, key_results: form.key_results.map((x, y) => y === i ? { ...x, label: e.target.value } : x) })} className="rounded-sm h-8 text-xs" data-testid={`okr-kr-label-${i}`} /></div>
                          <div className="col-span-3"><Label className="text-[10px]">Metric</Label><Input value={k.metric} onChange={(e) => setForm({ ...form, key_results: form.key_results.map((x, y) => y === i ? { ...x, metric: e.target.value } : x) })} className="rounded-sm h-8 text-xs" /></div>
                          <div className="col-span-2"><Label className="text-[10px]">Target</Label><Input type="number" value={k.target} onChange={(e) => setForm({ ...form, key_results: form.key_results.map((x, y) => y === i ? { ...x, target: e.target.value } : x) })} className="rounded-sm h-8 text-xs font-mono-plex" /></div>
                          <div className="col-span-2"><Label className="text-[10px]">Current</Label><Input type="number" value={k.current} onChange={(e) => setForm({ ...form, key_results: form.key_results.map((x, y) => y === i ? { ...x, current: e.target.value } : x) })} className="rounded-sm h-8 text-xs font-mono-plex" /></div>
                          <div className="col-span-1 pb-0.5">{form.key_results.length > 1 && <button onClick={() => setForm({ ...form, key_results: form.key_results.filter((_, y) => y !== i) })} className="text-red-600 hover:opacity-70"><Trash size={14} /></button>}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={save} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="okr-save-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatBlock label="Total objectives" value={flat.length} />
        <StatBlock label="Overall progress" value={`${overallProgress}%`} />
        <StatBlock label="Company OKRs" value={flat.filter((f) => f.level === "company").length} />
        <StatBlock label="Completed" value={flat.filter((f) => (f.progress_percent || 0) >= 100).length} />
      </div>

      <div className="space-y-3" data-testid="okr-tree">
        {tree.roots.length === 0 && <div className="border border-zinc-200 bg-white p-12 rounded-sm text-center"><Target size={32} className="mx-auto text-zinc-300" /><div className="text-sm text-zinc-500 mt-2">No objectives for {quarter} yet.</div></div>}
        {flattenTree(tree.roots).map(({ node, depth }) => <ObjectiveCard key={node.id} node={node} depth={depth} onUpdate={load} isHR={isHR} />)}
      </div>
    </div>
  );
}

function flattenTree(roots) {
  const out = [];
  const walk = (nodes, depth) => {
    for (const n of nodes) {
      out.push({ node: n, depth });
      if (n.children && n.children.length) walk(n.children, depth + 1);
    }
  };
  walk(roots, 0);
  return out;
}

function StatBlock({ label, value }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className="font-display text-3xl font-bold text-adb-navy mt-1">{value}</div>
    </div>
  );
}

function ObjectiveCard({ node, depth, onUpdate, isHR }) {
  const [editing, setEditing] = useState(false);
  const [krs, setKrs] = useState(node.key_results || []);

  const save = async () => {
    try {
      await api.patch(`/okr/objectives/${node.id}`, { key_results: krs.map((k) => ({ ...k, target: Number(k.target), current: Number(k.current) })) });
      toast.success("Progress updated"); setEditing(false); onUpdate();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async () => { if (!window.confirm("Delete objective?")) return; try { await api.delete(`/okr/objectives/${node.id}`); onUpdate(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div style={{ marginLeft: depth * 24 }} className={depth > 0 ? "border-l-2 border-adb-navy/20 pl-3" : ""}>
      <div className="border border-zinc-200 bg-white rounded-sm hover:border-adb-navy/40 transition-colors">
        <div className="p-4">
          <div className="flex items-start gap-2">
            <div className="mt-1 shrink-0">{depth > 0 ? <CaretRight size={12} className="text-adb-navy/40" /> : <Target size={14} className="text-adb-navy" />}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`rounded-sm ${LEVEL_STYLE[node.level]} text-[10px] uppercase`}>{node.level}</Badge>
                <div className="font-display text-base font-bold">{node.title}</div>
                {node.owner_name && <span className="text-xs text-zinc-500">· {node.owner_name}</span>}
              </div>
              {node.description && <div className="text-xs text-zinc-500 mt-1">{node.description}</div>}
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 max-w-[300px]">
                  <div className="flex items-center justify-between text-[10px] font-mono-plex text-zinc-500 mb-0.5"><span>Progress</span><span className="font-semibold text-adb-navy">{node.progress_percent}%</span></div>
                  <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className={`h-full transition-all duration-500 ${node.progress_percent >= 100 ? "bg-adb-green" : node.progress_percent >= 60 ? "bg-emerald-600" : "bg-adb-navy"}`} style={{ width: `${Math.min(100, node.progress_percent)}%` }} />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isHR && !editing && <button onClick={() => setEditing(true)} className="text-xs px-2 py-1 border border-zinc-300 hover:border-adb-navy rounded-sm" data-testid={`okr-edit-${node.id}`}>Update</button>}
                  {isHR && <button onClick={del} className="text-red-600 hover:opacity-70 p-1"><Trash size={14} /></button>}
                </div>
              </div>
              {node.key_results.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1.5">
                  {(editing ? krs : node.key_results).map((k, i) => {
                    const pct = k.target > 0 ? Math.min(100, Math.round(100 * (k.current || 0) / k.target)) : 0;
                    return (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center text-xs">
                        <div className="col-span-4 truncate">{k.label}</div>
                        <div className="col-span-6">
                          <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                            <div className={`h-full ${pct >= 100 ? "bg-adb-green" : "bg-adb-navy"}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="col-span-2 font-mono-plex text-right">
                          {editing ? (
                            <input type="number" className="w-16 border border-zinc-200 rounded-sm px-1 text-right text-xs" value={k.current} onChange={(e) => setKrs(krs.map((x, y) => y === i ? { ...x, current: e.target.value } : x))} data-testid={`okr-kr-current-${node.id}-${i}`} />
                          ) : `${k.current || 0}/${k.target}`}
                        </div>
                      </div>
                    );
                  })}
                  {editing && (
                    <div className="pt-2 flex justify-end gap-2">
                      <button onClick={() => { setKrs(node.key_results); setEditing(false); }} className="text-xs px-2 py-1 border border-zinc-300 rounded-sm">Cancel</button>
                      <button onClick={save} className="text-xs px-2 py-1 bg-adb-navy text-white rounded-sm" data-testid={`okr-save-progress-${node.id}`}>Save progress</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
